import { toJpeg } from 'html-to-image';
import jsPDF from 'jspdf';

const MARGIN_MM = 10;
const PAGE_W_MM = 210;   // A4 portrait
const PAGE_H_MM = 297;
const CONTENT_W = PAGE_W_MM - MARGIN_MM * 2;   // 190 mm
const CONTENT_H = PAGE_H_MM - MARGIN_MM * 2;   // 277 mm

// ─── Step 1: Convert every <img> src to a base64 data URL ────────────────────
// html-to-image renders via SVG foreignObject. Cross-origin img srcs fail
// silently there. Pre-fetching and inlining them guarantees they appear.
async function inlineImages(el: HTMLElement): Promise<() => void> {
  const imgs = Array.from(el.querySelectorAll<HTMLImageElement>('img'));
  const saves: Array<{ img: HTMLImageElement; src: string }> = [];

  await Promise.allSettled(imgs.map(async img => {
    const original = img.src;
    if (!original || original.startsWith('data:')) return;
    try {
      const res  = await fetch(original, { cache: 'force-cache' });
      const blob = await res.blob();
      const b64  = await new Promise<string>((ok, fail) => {
        const r = new FileReader();
        r.onload  = () => ok(r.result as string);
        r.onerror = fail;
        r.readAsDataURL(blob);
      });
      saves.push({ img, src: original });
      img.src = b64;
      // Wait for the new src to finish loading. onerror too: a response that is
      // not an image never fires onload, and the capture would hang on it.
      if (!img.complete) await new Promise<void>(r => { img.onload = () => r(); img.onerror = () => r(); });
    } catch { /* leave src unchanged on error */ }
  }));

  // Return a restore function
  return () => saves.forEach(({ img, src }) => { img.src = src; });
}

// ─── Step 2: Find page-break positions that never cut through a <tr> ─────────
// or through elements marked with data-pdf-avoid-break="true".
// Uses getBoundingClientRect so it's independent of scroll position.
//
// A cut only moves off the raw page boundary when that boundary actually lands
// INSIDE a row/block (straddling it) — otherwise it stays at the full page
// height. Snapping back to "the nearest row above" unconditionally (regardless
// of how far above) was the bug: once the last <tr> in the whole document was
// behind us, every later page kept re-cutting at that same old row, leaving
// the rest of each page blank and pushing all remaining content forward.
function rowSafeBreaks(element: HTMLElement, totalHMm: number): number[] {
  const eRect    = element.getBoundingClientRect();
  const elHeight = eRect.height;                        // CSS px
  const scale    = totalHMm / elHeight;                 // mm per CSS px

  const toMmSpan = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return { top: (r.top - eRect.top) * scale, bottom: (r.bottom - eRect.top) * scale };
  };

  // Every <tr>'s span, in mm from element top
  const rowSpans = Array.from(element.querySelectorAll('tr'))
    .map(row => toMmSpan(row as HTMLElement))
    .filter(s => s.bottom > 0 && s.top < totalHMm);

  // Blocks that must not be split — their top edge is a preferred break point
  const avoidBlocks = Array.from(element.querySelectorAll<HTMLElement>('[data-pdf-avoid-break]'))
    .map(toMmSpan)
    .filter(b => b.top >= 0 && b.bottom > b.top);

  // Build break points
  const breaks: number[] = [];
  let pageEnd   = CONTENT_H;
  let prevBreak = 0;

  while (pageEnd < totalHMm) {
    const straddledBlock = avoidBlocks.find(b => b.top < pageEnd && b.bottom > pageEnd);
    const straddledRow   = rowSpans.find(r => r.top < pageEnd && r.bottom > pageEnd);

    let cut = pageEnd;
    if (straddledBlock && straddledBlock.top > prevBreak) {
      cut = straddledBlock.top;
    } else if (straddledRow && straddledRow.top > prevBreak) {
      cut = straddledRow.top;
    }

    breaks.push(cut);
    prevBreak = cut;
    pageEnd   = cut + CONTENT_H;
  }

  return breaks;
}

// ─── Step 2b: Detect a canvas that rendered as essentially all-white ─────────
// Sampled rather than pixel-by-pixel since a genuinely blank slice should have
// zero non-white pixels anywhere; a real content slice hits one almost
// immediately (text, borders, colored fills), so the early-exit keeps this cheap.
function isCanvasBlank(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const { data } = ctx.getImageData(0, 0, width, height);
  const stride = 4 * 7; // sample every 7th pixel
  for (let i = 0; i < data.length; i += stride) {
    if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return false;
  }
  return true;
}

// ─── Core: capture an on-screen report element into a jsPDF document ─────────
// The report the client receives is a capture of the report the adviser was
// looking at — there is no second rendering of it anywhere. Both the Download
// button and the email attachment go through here.
const REPORT_READY_TIMEOUT_MS = 15000;

/**
 * Resolves once the report's figures have finished loading. BillReportScreen
 * marks itself `data-report-ready="false"` while it loads the retailer's charge
 * map, which its section rows are paired through. Capturing before then would
 * send a client a report with no section rows. An element without the
 * attribute (any other document) is ready at once.
 */
async function waitForReportReady(element: HTMLElement): Promise<void> {
  const flagged = element.hasAttribute('data-report-ready')
    ? element
    : element.querySelector<HTMLElement>('[data-report-ready]');
  if (!flagged) return;
  const started = Date.now();
  while (flagged.getAttribute('data-report-ready') !== 'true') {
    if (Date.now() - started > REPORT_READY_TIMEOUT_MS) {
      throw new Error('The report figures did not finish loading. Try again.');
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  // One more paint so the rows that just arrived are laid out before capture.
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

async function buildReportPdf(element: HTMLElement): Promise<jsPDF> {
  await waitForReportReady(element);

  // 1. Remove overflow:hidden from ancestors (prevents canvas clipping)
  type OvSave = { el: HTMLElement; ov: string; ox: string; oy: string };
  const ovSaved: OvSave[] = [];
  let cur: HTMLElement | null = element.parentElement;
  while (cur && cur !== document.documentElement) {
    const cs = window.getComputedStyle(cur);
    if (cs.overflow === 'hidden' || cs.overflowX === 'hidden' || cs.overflowY === 'hidden') {
      ovSaved.push({ el: cur, ov: cur.style.overflow, ox: cur.style.overflowX, oy: cur.style.overflowY });
      cur.style.overflow = cur.style.overflowX = cur.style.overflowY = 'visible';
    }
    cur = cur.parentElement;
  }

  // Pin element to a fixed render width so fonts scale correctly in PDF.
  // Landscape A4 content = 277mm. At 96dpi, 1100px → 1mm ≈ 3.98px.
  // 18px base font × (277mm / 1100px) = 4.5mm — comfortably readable in print.
  const prevWidth    = element.style.width;
  const prevMaxWidth = element.style.maxWidth;
  const prevMinWidth = element.style.minWidth;
  element.style.width    = '794px';
  element.style.maxWidth = '794px';
  element.style.minWidth = '794px';

  // `r()` rather than passing `r` straight to requestAnimationFrame: rAF hands
  // its callback a DOMHighResTimeStamp, which would resolve this Promise<void>
  // with a number.
  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));

  // 2. Inline all images as base64 so SVG foreignObject renders them
  const restoreImages = await inlineImages(element);

  try {
    // 3. Capture the element as a single tall JPEG. html-to-image rejects with
    // the DOM Event, not an Error, when an image it embeds fails to load (the
    // logo server unreachable, say). Turned into an Error here so every caller
    // can say what went wrong instead of a generic "could not be sent". Still a
    // failure on purpose: a client report without its branding is not sent.
    const dataUrl = await toJpeg(element, {
      quality:         0.99,
      backgroundColor: '#ffffff',
      pixelRatio:      3,
    }).catch((err: unknown) => {
      if (err instanceof Error) throw err;
      throw new Error('The report could not be captured: an image on it (such as the logo) failed to load. Check the connection and try again.');
    });

    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((ok, fail) => {
      img.onload = () => ok();
      img.onerror = () => fail(new Error('The captured report image could not be read. Try again.'));
    });

    // 4. Calculate total height in mm (pixelRatio:2 → canvas is 2× element CSS px)
    const totalHMm = (img.height / 2 / img.width * 2) * CONTENT_W;
    // Simplified: totalHMm = (cssHeight / cssWidth) * CONTENT_W
    // img.height = cssHeight*2, img.width = cssWidth*2 → ratio is preserved
    const totalHMmFinal = (img.height / img.width) * CONTENT_W;

    // 5. Find row-safe page break positions
    const breaks = rowSafeBreaks(element, totalHMmFinal);

    // Build page slices [[startMm, endMm], ...]
    const slices: [number, number][] = [];
    let prev = 0;
    for (const b of breaks) { slices.push([prev, b]); prev = b; }
    slices.push([prev, totalHMmFinal]);

    // Drop negligible slivers (e.g. a trailing near-zero-height slice) so no blank page is appended
    const nonEmptySlices = slices.filter(([s, e]) => e - s > 1);

    // 6. Render each slice onto a PDF page — but never consume a page for a
    // slice that renders as blank (e.g. trailing space below the last real
    // content, from box measurement quirks unrelated to actual page content).
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let pagesAdded = 0;

    nonEmptySlices.forEach(([startMm, endMm]) => {
      const sliceHMm = endMm - startMm;
      const srcY     = Math.round((startMm / totalHMmFinal) * img.height);
      const srcH     = Math.round((sliceHMm / totalHMmFinal) * img.height);
      if (srcH <= 0) return;

      const canvas = document.createElement('canvas');
      canvas.width  = img.width;
      canvas.height = srcH;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, srcY, img.width, srcH, 0, 0, img.width, srcH);

      if (isCanvasBlank(ctx, canvas.width, canvas.height)) return;

      if (pagesAdded > 0) pdf.addPage('a4', 'portrait');
      pagesAdded++;
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.99), 'JPEG', MARGIN_MM, MARGIN_MM, CONTENT_W, sliceHMm);
    });

    return pdf;

  } finally {
    restoreImages();
    element.style.width    = prevWidth;
    element.style.maxWidth = prevMaxWidth;
    element.style.minWidth = prevMinWidth;
    ovSaved.forEach(({ el, ov, ox, oy }) => {
      el.style.overflow = ov; el.style.overflowX = ox; el.style.overflowY = oy;
    });
  }
}

/** Triggers a browser download of the report currently on screen. */
export async function downloadReportAsPdf(element: HTMLElement, filename: string): Promise<void> {
  const pdf = await buildReportPdf(element);
  pdf.save(filename);
}

/**
 * The same PDF as `downloadReportAsPdf`, handed back as a Blob so it can be
 * emailed instead of saved. Byte-for-byte the same document — the client's copy
 * and the adviser's copy cannot diverge because they are produced by one
 * function from one DOM node.
 */
export async function renderReportPdfBlob(element: HTMLElement): Promise<Blob> {
  const pdf = await buildReportPdf(element);
  return pdf.output('blob');
}

// ─── HTML copy of the same report ────────────────────────────────────────────
// Sent alongside the PDF so the client can open the report in a browser, copy
// figures out of it and read it on a phone. Serialised from the same DOM node
// the PDF is captured from — not a second template — so the two copies cannot
// disagree.

async function toDataUrl(src: string): Promise<string | null> {
  try {
    const res  = await fetch(src, { cache: 'force-cache' });
    const blob = await res.blob();
    return await new Promise<string>((ok, fail) => {
      const r = new FileReader();
      r.onload  = () => ok(r.result as string);
      r.onerror = fail;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Rules on these selectors style the page rather than any one node — Tailwind's
// `*, ::before, ::after` variable reset, preflight on html/body, :root tokens.
const DOCUMENT_LEVEL_SELECTOR = /(^|[\s,>+~(])(\*|:root|html|body)(?=$|[\s,.:#[>+~)])/;

/**
 * The app's CSS, cut down to the rules the report actually uses. The whole
 * bundle carries every editor's stylesheet; the report needs a few hundred
 * Tailwind rules.
 */
function collectReportCss(element: HTMLElement): { css: string; links: string[] } {
  const usedBy = (selectorText: string): boolean => {
    if (DOCUMENT_LEVEL_SELECTOR.test(selectorText)) return true;
    // Pseudo-elements never match querySelector; the element they hang off does.
    const plain = selectorText.replace(/::?(before|after|placeholder|marker|selection|backdrop|-webkit-[\w-]+|-moz-[\w-]+)/g, '');
    try {
      return element.matches(plain) || element.querySelector(plain) !== null;
    } catch {
      return false;
    }
  };

  const keep = (rules: CSSRuleList): string => {
    let out = '';
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSStyleRule) {
        // An embedded file (an editor's icon sprite, say) is never the report's.
        if (usedBy(rule.selectorText) && !rule.cssText.includes('url(data:')) out += rule.cssText + '\n';
      } else if (rule instanceof CSSMediaRule || rule instanceof CSSSupportsRule) {
        const inner = keep(rule.cssRules);
        if (inner) out += `@${rule instanceof CSSMediaRule ? 'media' : 'supports'} ${rule.conditionText} {\n${inner}}\n`;
      }
      // Everything else is dropped. PDF + HTML went past Vercel's 4.5 MB request
      // limit (413 on every send, 2026-10-14) and @font-face is where a bundle
      // keeps base64 font files; the report sets system fonts and does not
      // animate, so it needs neither those nor @keyframes.
    }
    return out;
  };

  let css = '';
  const links: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      css += keep(sheet.cssRules);
    } catch {
      // Cross-origin sheet (a web font, say): its rules can't be read, so link it.
      if (sheet.href) links.push(sheet.href);
    }
  }
  return { css, links };
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The report currently on screen as one self-contained HTML file: styles and
 * images inlined, so it opens offline from the email with nothing to fetch but
 * any cross-origin font.
 */
export async function renderReportHtmlBlob(element: HTMLElement, title = 'Bill Validation Report'): Promise<Blob> {
  await waitForReportReady(element);

  const clone = element.cloneNode(true) as HTMLElement;
  const imgs = Array.from(clone.querySelectorAll<HTMLImageElement>('img'));
  const inlined = new Map<string, string | null>();
  await Promise.all(imgs.map(async (img) => {
    const src = img.src;
    if (!src || src.startsWith('data:')) return;
    if (!inlined.has(src)) inlined.set(src, await toDataUrl(src));
    const data = inlined.get(src);
    if (data) img.src = data;
    // A lazy image that never scrolled into view would stay blank in the file.
    img.removeAttribute('loading');
    img.removeAttribute('srcset');
  }));

  const { css, links } = collectReportCss(element);
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
${links.map((href) => `<link rel="stylesheet" href="${escapeHtml(href)}">`).join('\n')}
<style>
${css}
</style>
<style>
  body { margin: 0; padding: 24px 16px; background: #f3f4f6; }
  body > [data-pdf-target] { margin: 0 auto; }
  @media print { body { padding: 0; background: #fff; } body > [data-pdf-target] { box-shadow: none; } }
</style>
</head>
<body>
${clone.outerHTML}
</body>
</html>
`;
  return new Blob([html], { type: 'text/html;charset=utf-8' });
}
